<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { ref, computed, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useNotification } from "@kyvg/vue3-notification";
import _ from 'lodash';
import MediaflowService from '@/services/MediaflowService';
import StreamStatsService from '@/services/StreamStatsService';
import Spinner from "@/components/ui/Spinner.vue";
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import ModalBox from '@/components/modals/ModalBox.vue';
import StreamSourceDropdown from '@/components/ui/StreamSourceDropdown.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import IconBase from '../../components/icon/IconBase.vue';
import BackButton from '@/components/Atoms/BackButton.vue'
import IconBin from '../../components/icon/IconBin.vue';
import FormSwitch from "@/components/Atoms/FormSwitch.vue";
import Title from "@/components/ui/Title.vue";
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue';
import IconMediaflowLockedPlaceholderImage from '../../components/icon/IconMediaflowLockedPlaceholderImage.vue';
import StreamPlayer from '@/components/ui/StreamPlayer.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import draggable from 'vuedraggable';
import IconEdit from '../../components/icon/IconEdit.vue';
import AddFlowModal from '@/components/modals/AddFlowModal.vue';
import EditFlowModal from '@/components/modals/EditFlowModal.vue';
import AddFlowMediaModal from '@/components/modals/AddFlowMediaModal.vue';
import EditMediaflowModal from '@/components/modals/EditMediaflowModal.vue';
import MediaTransformDropdownPanel from '@/components/Mediaflow/MediaTransformDropdownPanel.vue'
import HeaderHelperLinks from '@/components/Moleculas/HeaderHelperLinks.vue';
import TabGroup from '@/components/Atoms/TabGroup.vue';
import TabItem from '@/components/Atoms/TabItem.vue';
import AddMediaflowAdScheduleModal from '@/components/modals/AddMediaflowAdScheduleModal.vue';
import IconMediaflowSingleTime from '@/components/icon/IconMediaflowSingleTime.vue';
import IconMediaflowRecurringTrigger from '@/components/icon/IconMediaflowRecurringTrigger.vue';
import IconMediaflowMultiAdBlock from '@/components/icon/IconMediaflowMultiAdBlock.vue';
import IconMediaflowSceneRotation from '@/components/icon/IconMediaflowSceneRotation.vue';
import IconMediaflowTimeSlotBlock from '@/components/icon/IconMediaflowTimeSlotBlock.vue';
import IconChevronDown from '@/components/icon/IconChevronDown.vue';
import Badge from '@/components/Badge.vue';
import IconTriangleUp from '@/components/icon/IconTriangleUp.vue';
import IconTriangleDown from '@/components/icon/IconTriangleDown.vue';
import IconPlusSmall from '@/components/icon/IconPlusSmall.vue';
import IconMediaflowFlowLines from '@/components/icon/IconMediaflowFlowLines.vue';
import IconMediaflowVolumeIndicator from '@/components/icon/IconMediaflowVolumeIndicator.vue';
import IconCornerFrame from '@/components/icon/IconCornerFrame.vue';
import IconOpenExternal from '@/components/icon/IconOpenExternal.vue';
import IconCog from '@/components/icon/IconCog.vue';
import IconPlay from '@/components/icon/IconPlay.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { notify } = useNotification();
const instance = getCurrentInstance();
const root = instance.proxy.$root;
const source = ref(null);

const resolutionDropdown = ref(null);
const resolutionAliasDropdown = ref(null);
const bitrateDropdown = ref(null);
const fpsDropdown = ref(null);

const mediaflow = computed(() => store.getters['Mediaflows/mediaflow']);
const mediaflowReady = computed(
  () => String(mediaflow.value?._id || '') === String(route.params.id || '')
);
const detailResolving = ref(true);
const detailSyncing = ref(false);
const detailRequestToken = ref(0);
const maxMediaflows = computed(() => store.getters['User/maxMediaflows']);
const mediaflowResolution = computed(() => store.getters['User/mediaflowResolution']);
const maxMediaflowFlows = computed(() => store.getters['User/maxMediaflowFlows']);
const maxMediaflowMedia = computed(() => store.getters['User/maxMediaflowMedia']);
const isLiveSubscriptionExpired = computed(() => store.getters['User/isLiveSubscriptionExpired']);
const hasLiveSubscription = computed(() => store.getters['User/hasLiveSubscription']);
const allInOneStreams = computed(() => store.getters['Streams/allInOneStreams'])
const mediaflowTrial = computed(() => store.getters['User/mediaflowTrial'])
const mediaflowAllowedFpsList = computed(() => store.getters['User/mediaflowAllowedFpsList'])
const mediaflowAdSchedule = computed(() => store.getters['User/mediaflowAdSchedule'])
const maxMediaflowAdSchedule = computed(() => store.getters['User/maxMediaflowAdSchedule']);

const fpsOptions = computed(() => {
  const fpsList = mediaflowAllowedFpsList.value;
  const defaultFps = '25,30';
  const fpsString = fpsList && fpsList.trim() ? fpsList : defaultFps;
  return fpsString.split(',').map(fps => fps.trim()).filter(fps => fps);
})

const modalDEMOVideo = ref(null);
const selectedTab = ref('flow');
const destination = ref(null)

// Tab management functions
const setTab = (tab) => {
  selectedTab.value = tab;
};

const isTabActive = (tab) => {
  return selectedTab.value === tab;
};
const search = ref('')
const startStreamProcessing = ref(false)

const transitionDropdown = ref(null);

// Refs for Flows and Sources management
const selectedFlowForSources = ref(null);
const slectedFlowMediaSource = ref(null);
const addFlowModalRef = ref(null);
const editFlowModalRef = ref(null);
const addFlowMediaModalRef = ref(null);
const editMediaflowModalRef = ref(null);
const addAdScheduleModalRef = ref(null);
const flowToDelete = ref(null);
const flowToEdit = ref(null);
const mediaToDelete = ref({});

// Ad Schedules
const adSchedules = ref([]);
const scheduleToDelete = ref(null);
const togglingScheduleId = ref(null);

// Stream Configuration Modal
const showStreamConfigModal = ref(false);
const streamConfigResolution = ref('720');
const streamConfigBitrate = ref('2500');
const streamConfigFps = ref('');
const fpsDropdownOpen = ref(false);
const streamConfigSaving = ref(false);

const showMediaPlayerModal = ref(false);
const mediaPlayerUrl = ref('');
const openMediaPlayerSelected = ref({ name: '' })
const mediaPlayerStream = ref(null);
const mediaPlayerMediaPulse = ref({});

const mediaPulse = ref(null)
const scopeAlive = ref(true)
const mediaPulseTimeoutCtrl = ref(null)
const streamAlive = ref(false)

const mediaPulsePreview = ref(null)
const mediaPulsePreviewTimeoutCtrl = ref(null)
const streamAlivePreview = ref(false)

const selectedResolution = ref('1080p')
const selectedResolutionAlias = ref('1920 x 1080')
const resolutionTypes = ref([
  { label: '1920 x 1080', value: '1080p' },
  { label: '1080 x 1920', value: '1080p' },
  { label: '1280 x 720', value: '720p' },
  { label: '720 x 1280', value: '720p' }
])
const resolutionTypes720Allowed = ref([
  { label: '1280 x 720', value: '720p' },
  { label: '720 x 1280', value: '720p' }
])
const resolutionTypes720Locked = ref([
  { label: '1920 x 1080', value: '1080p' },
  { label: '1080 x 1920', value: '1080p' }
])

const getResolutionAliasesByPreset = (preset) => {
  return resolutionTypes.value
    .filter((option) => option.value === preset)
    .map((option) => option.label)
}

const getResolvedScreenResolution = (preset, screenResolution) => {
  const aliases = getResolutionAliasesByPreset(preset)
  if (!aliases.length) return ''
  if (screenResolution && aliases.includes(screenResolution)) {
    return screenResolution
  }
  return aliases[0]
}

const portraitResolutions = ['720 x 1280', '1080 x 1920']
const isPortraitResolution = computed(() => {
  const currentScreenResolution = mediaflow.value?.screenResolution
  return portraitResolutions.includes(currentScreenResolution)
})

const sesessionStateCleared = ref(false);
const sceneTransition = ref('Fade')
const sceneTransitionProcessing = ref(false)
const sceneTransitionDuration = ref('300 ms')

const isFocused = ref(false)

const onResolutionSelect = (option) => {
  selectedResolution.value = option.value
  selectedResolutionAlias.value = option.label
  resolutionDropdown.value.hide(true);
}


const destinationStream = computed(() => {
  if (!destination.value) return null;
  return allInOneStreams.value.find(stream => stream._id === destination.value._id);
})

const disabledStreamIds = computed(() => {
  const ids = new Set();

  if (destinationStream.value?._id) {
    ids.add(destinationStream.value._id);
  }

  mediaflow.value?.flows?.forEach(flow => {
    flow?.media?.forEach(mediaItem => {
      if (mediaItem?.type === 'live') {
        const streamValue = mediaItem?.stream;
        const streamId = typeof streamValue === 'object' && streamValue !== null
          ? (streamValue._id || streamValue.id)
          : streamValue;
        if (streamId) {
          ids.add(streamId);
        }
      }
    });
  });

  return Array.from(ids);
});

// Tokens stop stale loops from rescheduling after an unsubscribe/resubscribe
// while their request was still in flight
let pulseLoopToken = 0
let pulsePreviewLoopToken = 0

const subscribeMediaPulse = () => {
  const streamKey = mediaflow.value?.key
  const streamEnabled = mediaflow.value?.enabled;
  clearTimeout(mediaPulseTimeoutCtrl.value);
  const token = ++pulseLoopToken;
  (async function loop() {
    if (!scopeAlive.value || token !== pulseLoopToken) return

    let timeoutInterval = 5000
    let shouldUpdatePulse = streamEnabled
    if (document.hidden) {
      // shouldUpdatePulse = false // cause blink of player once window out of focus
      timeoutInterval = 6000
    }

    if (shouldUpdatePulse) {
      if (!document.hasFocus()) {
        timeoutInterval = 14000
      }
    }
    if (shouldUpdatePulse) {
      try {
        const pulseObject = await StreamStatsService.getStreamMediaPulse(
          streamKey
        )
        onMediaPulse(pulseObject);

      } catch (e) { }
    } else mediaPulse.value = null
    // reschedule
    if (!scopeAlive.value || token !== pulseLoopToken) return
    mediaPulseTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
  })();
}

const onMediaPulse = (pulseObject = {}) => {
  if (mediaPulse.value?.withoutPushStats) {
    const oldPushStats = mediaPulse.value.push_stats;
    pulseObject.push_stats = oldPushStats;
  }

  mediaPulse.value = pulseObject;
  streamAlive.value =
    mediaflow.value.enabled && mediaPulse.value?.alive;
}

const onMediaPulseChanged = async () => {
}

const unsubscribeMediaPulse = () => {
  pulseLoopToken++;
  clearTimeout(mediaPulseTimeoutCtrl.value);
}

const subscribeMediaPulsePreview = () => {
  const streamKey = mediaflow.value?.key + '_preview'
  const streamEnabled = mediaflow.value?.enabled;
  clearTimeout(mediaPulsePreviewTimeoutCtrl.value);
  const token = ++pulsePreviewLoopToken;
  (async function loop() {
    if (!scopeAlive.value || token !== pulsePreviewLoopToken) return

    let timeoutInterval = 5000
    let shouldUpdatePulse = streamEnabled
    if (document.hidden) {
      // shouldUpdatePulse = false // cause blink of player once window out of focus
      timeoutInterval = 6000
    }

    if (shouldUpdatePulse) {
      if (!document.hasFocus()) {
        timeoutInterval = 14000
      }
    }
    if (shouldUpdatePulse) {
      try {
        const pulseObject = await StreamStatsService.getStreamMediaPulse(
          streamKey
        )
        onMediaPulsePreview(pulseObject);

      } catch (e) { }
    } else mediaPulsePreview.value = null
    // reschedule
    if (!scopeAlive.value || token !== pulsePreviewLoopToken) return
    mediaPulsePreviewTimeoutCtrl.value = setTimeout(loop, timeoutInterval);
  })();
}

const onMediaPulsePreview = (pulseObject = {}) => {
  if (mediaPulsePreview.value?.withoutPushStats) {
    const oldPushStats = mediaPulsePreview.value.push_stats;
    pulseObject.push_stats = oldPushStats;
  }

  mediaPulsePreview.value = pulseObject;
  streamAlivePreview.value =
    mediaflow.value.enabled && mediaPulsePreview.value?.alive;
}

const onMediaPulsePreviewChanged = async () => {
}

const unsubscribeMediaPulsePreview = () => {
  pulsePreviewLoopToken++;
  clearTimeout(mediaPulsePreviewTimeoutCtrl.value);
}

const toggleMediaflow = async () => {
  try {
    await MediaflowService.toggleMediaflow(mediaflow.value._id);
    store.dispatch('Mediaflows/toggleMediaflow', { id: mediaflow.value._id });
    notify({ group: "success", text: `Cloud Production ${mediaflow.value.enabled ? 'enabled' : 'disabled'}` });
  } catch (err) {
    console.error('Failed to toggle mediaflow', err);
    notify({ group: "error", text: err.message || "Cloud Production source update failed" });
  }
};

const onMediaflowDelete = async () => {
  if (!mediaflow.value._id) return;
  try {
    await MediaflowService.deleteMediaflow(mediaflow.value._id);
    store.dispatch('Mediaflows/removeMediaflow', { id: mediaflow.value._id });
    notify({ group: "success", text: "Cloud Production deleted" });
    router.push({ name: "MediaflowDashboard" });
  } catch (err) {
    console.error('Failed to delete mediaflow', err);
    notify({ group: "error", text: err.message || "Cloud Production delete failed" });
  }
};

const onDeleteMediaflowRequest = () => {
  showModal('delete-mediaflow-content');
};

const onNameChange = async (newName) => {
  try {
    await MediaflowService.updateMediaflows(
      mediaflow.value._id,
      {
        name: newName
      }
    )
    store.dispatch('Mediaflows/setName', { id: mediaflow.value._id, name: newName });
    notify({ group: "success", text: "Cloud Production name updated" });
  } catch (err) {
    console.error('Failed to update mediaflow name', err);
    notify({ group: "error", text: err.message || "Unable to update the name." });
  }
};

const onStreamStart = async () => {
  startStreamProcessing.value = true
  try {
    const res = await MediaflowService.startStream(mediaflow.value._id);
    notify({ group: "success", text: "Stream started" });

    if (res && res.data) {
      store.dispatch('Mediaflows/setObsInstance', {
        mediaflowId: mediaflow.value._id,
        obsInstance: res.data.id,
        flowInstanceReservedAt: res.data.flowInstanceReservedAt,
        flowInstanceReserveTimeSec: res.data.flowInstanceReserveTimeSec
      });

      sesessionStateCleared.value = false
    }
  } catch (err) {
    console.error(err)
    notify({ group: "error", text: err || "Unable to start the stream." });
  }
  startStreamProcessing.value = false
};

const onStreamStop = async () => {
  startStreamProcessing.value = true
  try {
    await MediaflowService.stopStream(mediaflow.value._id);
    notify({ group: "success", text: "Stream stopped." });

    store.dispatch('Mediaflows/clearObsInstance', mediaflow.value._id);
    if (obsCountdownInterval)
      clearInterval(obsCountdownInterval);
    if (obsClearTimeout) {
      clearTimeout(obsClearTimeout);
      obsClearTimeout = null;
    }
    obsCountdown.value = 0;
  } catch (err) {
    console.error(err)
    notify({ group: "error", text: err || "Unable to stop the stream." });
  }
  startStreamProcessing.value = false
};

const onTransition = async () => {
  sceneTransitionProcessing.value = true;
  try {
    // Parse duration from "300 ms" format to just the number
    const durationStr = sceneTransitionDuration.value.toString().replace(' ms', '').trim();
    const duration = parseInt(durationStr) || 300;

    const transitionData = {
      transitionName: sceneTransition.value,
      duration: duration
    };

    await MediaflowService.transition(mediaflow.value._id, transitionData);
    notify({ group: "success", text: `Transition applied: ${sceneTransition.value}` });
  } catch (err) {
    console.error(err);
    notify({ group: "error", text: err?.message || "Transition failed" });
  }
  sceneTransitionProcessing.value = false;
};

const onTransitionTypeSelect = (typ) => {
  sceneTransition.value = typ.value
  transitionDropdown.value?.hide();
}

const onTransitionDurationChange = (event) => {
  let duration = parseInt(event.target.value);

  if (isNaN(duration)) duration = 300;

  duration = Math.max(50, duration);
  duration = Math.min(10000, duration);

  event.target.value = duration;
  sceneTransitionDuration.value = duration + ' ms';
};

const adjustTransitionDuration = (direction) => {
  const currentDuration = parseInt(sceneTransitionDuration.value.toString().replace(' ms', '').trim()) || 300;

  let newDuration = direction === 'up' ? currentDuration + 50 : currentDuration - 50;

  newDuration = Math.max(50, Math.min(10000, newDuration));
  sceneTransitionDuration.value = newDuration + ' ms';
};

const ensureTransitionDurationRange = (event) => {
  let duration = parseInt(event.target.value);

  if (isNaN(duration)) duration = 300;

  duration = Math.max(50, duration);
  duration = Math.min(10000, duration);
  event.target.value = duration;

  sceneTransitionDuration.value = duration + ' ms';
};

const onSourceDropdownShow = () => {
  // Handler for when source dropdown is shown
};

const onSourceDropdownHide = () => {
  // Handler for when source dropdown is hidden
};

const setDestination = async (stream) => {
  try {
    const destinationId = stream ? stream._id : null;

    await MediaflowService.updateMediaflows(
      mediaflow.value._id,
      {
        destination: destinationId
      }
    );

    destination.value = stream
    await store.dispatch('Mediaflows/setDestination', {
      id: mediaflow.value._id,
      destination: destinationId
    });
    await store.dispatch('Mediaflows/getMediaflow', mediaflow.value._id);
    setTimeout(() => {
      source.value?.hide(true);
    }, 100)

    notify({
      group: "success",
      text: destinationId ? "Destination updated" : "Destination removed"
    });
  } catch (err) {
    setTimeout(() => {
      source.value?.hide(true);
    }, 100)

    console.error('Failed to update destination', err);
    notify({
      group: "error",
      text: err.message || "Destination update failed"
    });
  }
}

const obsCountdown = ref(0);
let obsCountdownInterval = null;
let obsClearTimeout = null;

const updateObsCountdown = () => {
  if (!mediaflow.value?.flowInstanceReservedAt || !mediaflow.value?.flowInstanceReserveTimeSec) {
    obsCountdown.value = 0;
    return;
  }
  const reservedAt = new Date(mediaflow.value.flowInstanceReservedAt).getTime();

  const now = Date.now();
  const elapsed = Math.floor((now - reservedAt) / 1000);
  const total = mediaflow.value.flowInstanceReserveTimeSec;
  obsCountdown.value = Math.max(total - elapsed, 0);

  // Check if countdown has reached 0 and clear OBS instance after 10 seconds
  if (obsCountdown.value === 0 && mediaflow.value?.obsInstance) {
    // Clear any existing timeout
    if (obsClearTimeout) {
      clearTimeout(obsClearTimeout);
    }

    // Set timeout to clear OBS instance after 10 seconds (additional delay as requested)
    setTimeout(() => {
      if (sesessionStateCleared.value) {
        return
      }

      store.dispatch('Mediaflows/clearObsInstance', mediaflow.value._id);
      sesessionStateCleared.value = true

      notify({
        group: "info",
        text: "Stream session expired"
      });
    }, 8000); // 10 seconds delay
  }
};

watch(
  () => mediaflow.value?.flowInstanceReservedAt,
  () => {
    // Clear any existing timeout when flowInstanceReservedAt changes
    if (obsClearTimeout) {
      clearTimeout(obsClearTimeout);
      obsClearTimeout = null;
    }

    updateObsCountdown();
    if (obsCountdownInterval)
      clearInterval(obsCountdownInterval);
    if (mediaflow.value?.flowInstanceReservedAt && mediaflow.value?.flowInstanceReserveTimeSec) {
      obsCountdownInterval = setInterval(updateObsCountdown, 1000);
    }
  },
  { immediate: true }
);

// Watch for OBS instance changes to clean up timeout when instance is cleared
watch(
  () => mediaflow.value?.obsInstance,
  (newInstance) => {
    if (!newInstance && obsClearTimeout) {
      clearTimeout(obsClearTimeout);
      obsClearTimeout = null;
    }
  }
);
watch(mediaflow, async () => {
  if (mediaflow.value) {
    unsubscribeMediaPulse()
    subscribeMediaPulse()
    unsubscribeMediaPulsePreview()
    subscribeMediaPulsePreview()
  }
})

watch(mediaPulse, async () => {
  await onMediaPulseChanged()
})

watch(mediaPulsePreview, async () => {
  await onMediaPulsePreviewChanged()
})

onUnmounted(() => {
  detailRequestToken.value += 1;
  if (obsCountdownInterval)
    clearInterval(obsCountdownInterval);

  if (obsClearTimeout) {
    clearTimeout(obsClearTimeout);
  }

  scopeAlive.value = false
  unsubscribeMediaPulse()
  unsubscribeMediaPulsePreview()
});

const formattedObsCountdown = computed(() => {
  const sec = obsCountdown.value;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;

  if (h > 0) {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }

  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
});

const showStopStreamModal = () => {
  showModal('confirm-stop-stream');
};

const onConfirmStopStream = () => {
  onStreamStop();
};

// Flow management functions
const openAddFlowModal = () => {
  addFlowModalRef.value?.toggleSidebar();
};

const openAddAdScheduleModal = () => {
  addAdScheduleModalRef.value?.toggleSidebar();
};

const fetchAdSchedules = async (mediaflowId = mediaflow.value?._id) => {
  if (!mediaflowId) return;
  try {
    const schedules = await MediaflowService.getAdSchedules(mediaflowId);
    if (!scopeAlive.value || String(route.params.id) !== String(mediaflowId)) return;
    adSchedules.value = schedules || [];
  } catch (err) {
    if (!scopeAlive.value || String(route.params.id) !== String(mediaflowId)) return;
    console.error('Failed to fetch ad schedules', err);
    notify({ group: 'error', text: err?.message || 'Failed to load ad schedules' });
  }
};

const onScheduleAdded = async () => {
  // Refresh the list after creating a new schedule
  await fetchAdSchedules();
};

const getScheduleTypeLabel = (type) => {
  const typeMap = {
    'single-time': 'Single Time',
    'recurring': 'Recurring',
    'multi-ad-block': 'Multi Ad Block',
    'scene-rotation': 'Scene Rotation',
    'time-slot-block': 'Time Slot Block'
  };
  return typeMap[type] || type;
};

const getScheduleIcon = (type) => {
  const iconMap = {
    'single-time': IconMediaflowSingleTime,
    'recurring': IconMediaflowRecurringTrigger,
    'multi-ad-block': IconMediaflowMultiAdBlock,
    'scene-rotation': IconMediaflowSceneRotation,
    'time-slot-block': IconMediaflowTimeSlotBlock
  };
  return iconMap[type] || null;
};

const toggleSchedule = async (schedule) => {
  if (!mediaflow.value?._id || !schedule._id || togglingScheduleId.value === schedule._id) return;

  togglingScheduleId.value = schedule._id;
  try {
    await MediaflowService.toggleAdSchedule(mediaflow.value._id, schedule._id);
    // Refresh the list to get updated enabled status
    await fetchAdSchedules();
    notify({
      group: 'success',
      text: `Schedule ${schedule.enabled ? 'deactivated' : 'activated'}`
    });
  } catch (err) {
    console.error('Failed to toggle schedule', err);
    notify({ group: 'error', text: err?.message || 'Failed to toggle schedule' });
  } finally {
    togglingScheduleId.value = null;
  }
};

const onScheduleEdit = (schedule) => {
  // Open edit modal with schedule data
  addAdScheduleModalRef.value?.openForEdit(schedule);
};

const onScheduleDelete = (schedule) => {
  scheduleToDelete.value = schedule;
  showModal('confirm-delete-schedule');
};

const confirmDeleteSchedule = async () => {
  if (!scheduleToDelete.value || !mediaflow.value?._id) return;
  try {
    await MediaflowService.deleteAdSchedule(mediaflow.value._id, scheduleToDelete.value._id);
    adSchedules.value = adSchedules.value.filter(
      s => s._id !== scheduleToDelete.value._id
    );
    notify({ group: 'success', text: 'Schedule deleted' });
  } catch (err) {
    console.error('Failed to delete schedule', err);
    notify({ group: 'error', text: err?.message || 'Failed to delete schedule' });
  }
  scheduleToDelete.value = null;
};

const onFlowAdded = (newFlow) => {
  store.dispatch('Mediaflows/addFlow', { mediaflowId: mediaflow.value._id, flow: newFlow });
  // Auto-select the first flow if none selected
  if (!selectedFlowForSources.value && mediaflow.value.flows.length > 0) {
    selectedFlowForSources.value = mediaflow.value.flows[0];
  }
};

const onFlowClick = async (flow) => {
  selectedFlowForSources.value = flow;
  if (!flow.active) {
    try {
      await store.dispatch('Mediaflows/switchFlow', {
        mediaflowId: mediaflow.value._id,
        flowId: flow._id
      });
      notify({ group: 'success', text: 'Flow switched successfully' });
    } catch (err) {
      console.error('Failed to switch flow', err);
      notify({ group: 'error', text: err?.message || 'Failed to switch flow' });
    }
  }
};

const onFlowDelete = (flow) => {
  flowToDelete.value = flow;
  showModal('confirm-delete-flow');
};

const confirmDeleteFlow = async () => {
  if (!flowToDelete.value) return;
  try {
    await store.dispatch('Mediaflows/removeFlow', {
      mediaflowId: mediaflow.value._id,
      flowId: flowToDelete.value._id
    });
    // Clear selection if deleted flow was selected
    if (selectedFlowForSources.value?._id === flowToDelete.value._id) {
      selectedFlowForSources.value = mediaflow.value.flows.length > 0 ? mediaflow.value.flows[0] : null;
    }
    notify({ group: 'success', text: 'Flow deleted' });
  } catch (err) {
    console.error('Failed to delete flow', err);
    notify({ group: 'error', text: err?.message || 'Failed to delete flow' });
  }
  flowToDelete.value = null;
};

const onFlowEdit = (flow) => {
  flowToEdit.value = flow;
  editFlowModalRef.value?.toggleSidebar();
};

const onFlowUpdated = (updatedFlow) => {
  if (!updatedFlow) return;

  store.dispatch('Mediaflows/setFlowName', {
    mediaflowId: mediaflow.value._id,
    flowId: updatedFlow._id,
    name: updatedFlow.data.name
  });

  if (selectedFlowForSources.value?._id === updatedFlow._id) {
    selectedFlowForSources.value = { ...selectedFlowForSources.value, name: updatedFlow.data.name };
  }

  flowToEdit.value = null;
};

const onFlowsReorder = async () => {
  // TODO: Implement flow reordering on backend if needed
  notify({ group: 'info', text: 'Flow order updated' });
};

const openStreamConfigModal = () => {
  if (mediaflow.value) {
    const currentResolution = mediaflow.value.resolutionPreset || '720p';
    const currentScreenResolution = mediaflow.value.screenResolution;
    selectedResolution.value = currentResolution;
    selectedResolutionAlias.value = getResolvedScreenResolution(currentResolution, currentScreenResolution);
    streamConfigResolution.value = currentResolution.replace('p', '') || '720';

    streamConfigBitrate.value = (mediaflow.value.bitrate || 2500).toString();

    const currentFps = (mediaflow.value.fps || 0).toString();
    const availableFps = fpsOptions.value;
    const firstFps = availableFps.length > 0 ? availableFps[0] : '30';
    streamConfigFps.value = availableFps.includes(currentFps) ? currentFps : firstFps;
  } else {
    const firstFps = fpsOptions.value.length > 0 ? fpsOptions.value[0] : '30';
    streamConfigFps.value = firstFps;
  }

  showStreamConfigModal.value = true;
};

const onStreamPage = () => {
  if (mediaflow?.value?.destination) {
    const url = '/app/livestreams/' + mediaflow?.value?.destination._id;
    console.log('url', url)
    window.open(url, '_blank');
  }
};

const closeStreamConfigModal = () => {
  if (streamConfigSaving.value) return;

  showStreamConfigModal.value = false;
  streamConfigResolution.value = '720';
  streamConfigBitrate.value = '2500';
  const firstFps = fpsOptions.value.length > 0 ? fpsOptions.value[0] : '30';
  streamConfigFps.value = firstFps;
  fpsDropdownOpen.value = false;
  streamConfigSaving.value = false;
};

const openMediaPlayerModal = async (media) => {
  showMediaPlayerModal.value = true;
  openMediaPlayerSelected.value = media
  mediaPlayerStream.value = null;
  mediaPlayerMediaPulse.value = {};
  console.log(media)

  if (media.type === 'live') {
    const stream = allInOneStreams.value.find(stream => stream._id === media.stream)
    if (stream) {
      // Fetch mediaPulse for the stream
      try {
        const pulseObject = await StreamStatsService.getStreamMediaPulse(stream.key);
        console.log('pulse', pulseObject)
        mediaPlayerMediaPulse.value = pulseObject || {};

        mediaPlayerStream.value = stream;
        mediaPlayerUrl.value = `https://player.castr.com/${stream.key}`

      } catch (e) {
        console.error('Failed to fetch mediaPulse for stream', e);
        mediaPlayerMediaPulse.value = {};
      }
    }
  }

  if (media.type === 'vod') {
    mediaPlayerUrl.value = `https://player.castr.com/vod/${media.file.replace('.mp4', '')}?preview=true`

    if (media.embedUrl && media.embedUrl.includes('range=')) {
      mediaPlayerUrl.value = media.embedUrl + '&preview=true'
    }
    // if(media.downloadUrl && media.file) {
    //   const [timestamp, duration, id, abr] = media.file
    //   const match = (media?.downloadUrl || '').match(/\/(live_[a-zA-Z0-9]+)\//);

    //   const stream = allInOneStreams.value.find(stream => stream._id === media.stream)
    //   console.log('stream', stream, media)

    //   if (match) {
    //     mediaPlayerUrl.value = `https://player.castr.com/${match[1]}?range=${timestamp}-${duration}&abr=${abr}&namedHls=true`
    //   }

    // }
  }

  console.log(mediaPlayerUrl.value)
};

const closeMediaPlayerModal = () => {
  showMediaPlayerModal.value = false;
  openMediaPlayerSelected.value = { name: '' }
  mediaPlayerStream.value = null;
  mediaPlayerMediaPulse.value = {};
};

const saveStreamConfig = async () => {
  streamConfigSaving.value = true

  try {
    const updateData = {
      resolutionPreset: selectedResolution.value,
      screenResolution: selectedResolutionAlias.value,
      bitrate: parseInt(streamConfigBitrate.value),
      fps: parseInt(streamConfigFps.value)
    };

    await MediaflowService.updateMediaflows(
      mediaflow.value._id,
      updateData
    );

    store.dispatch('Mediaflows/setResolutionPreset', {
      id: mediaflow.value._id,
      resolutionPreset: updateData.resolutionPreset
    });
    store.dispatch('Mediaflows/setScreenResolution', {
      id: mediaflow.value._id,
      screenResolution: updateData.screenResolution
    });

    store.dispatch('Mediaflows/setBitrate', {
      id: mediaflow.value._id,
      bitrate: updateData.bitrate
    });

    store.dispatch('Mediaflows/setFps', {
      id: mediaflow.value._id,
      fps: updateData.fps
    });

    notify({
      group: 'success',
      text: 'Stream configuration saved successfully'
    });

    streamConfigSaving.value = false
    closeStreamConfigModal();
  } catch (err) {
    console.error('Failed to save stream config:', err);
    notify({
      group: 'error',
      text: err?.message || 'Failed to save stream configuration'
    });
  } finally {
    streamConfigSaving.value = false
  }
};

// Source management functions
const openAddSourceModal = () => {
  if (!selectedFlowForSources.value) return;
  addFlowMediaModalRef.value?.toggleSidebar();
};

const onMediaAdded = (mediaList) => {
  if (!selectedFlowForSources.value) return;

  store.dispatch('Mediaflows/clearFlowMedia', {
    mediaflowId: mediaflow.value._id,
    flowId: selectedFlowForSources.value._id
  });

  const list = Array.isArray(mediaList) ? mediaList : [mediaList];
  list.forEach(media => {
    // Initialize default values for new media
    if (typeof media.volume === 'undefined') media.volume = 100;
    if (typeof media.show === 'undefined') media.show = false;
    if (typeof media.muted === 'undefined') media.muted = false;
    if (typeof media.audioOffset === 'undefined') media.audioOffset = 0;

    store.dispatch('Mediaflows/addFlowMedia', {
      mediaflowId: mediaflow.value._id,
      flowId: selectedFlowForSources.value._id,
      media
    });
  });
};

const onMediaEdit = (flowId, media) => {
  editMediaflowModalRef.value?.toggleSidebar();
  slectedFlowMediaSource.value = media
};

const onMediaUpdated = (updatedMedia) => {
  if (!selectedFlowForSources.value) return;
  store.dispatch('Mediaflows/updateFlowMedia', {
    mediaflowId: mediaflow.value._id,
    flowId: selectedFlowForSources.value._id,
    mediaId: updatedMedia.id,
    media: updatedMedia
  });
};

const onMediaToggle = async (flowId, mediaId) => {
  try {
    await store.dispatch('Mediaflows/toggleFlowMediaShow', {
      mediaflowId: mediaflow.value._id,
      flowId,
      mediaId
    });
    notify({ group: 'success', text: 'Settings saved' });
  } catch (err) {
    console.error('Failed to toggle media visibility', err);
    notify({ group: 'error', text: 'Failed to toggle media show' });
  }
};

const onMediaDelete = (flowId, mediaId) => {
  mediaToDelete.value = { flowId, mediaId };
  showModal('modal-delete-media');
};

const confirmMediaDelete = async () => {
  const { flowId, mediaId } = mediaToDelete.value;
  try {
    await store.dispatch('Mediaflows/deleteFlowMedia', {
      mediaflowId: mediaflow.value._id,
      flowId,
      mediaId
    });
    notify({ group: 'success', text: 'Media deleted' });
  } catch (err) {
    console.error('Failed to delete media item', err);
    notify({ group: 'error', text: 'Failed to delete media' });
  }
};

const getMediaTypeName = (type) => {
  const types = {
    'live': 'Livestream',
    'image': 'Image',
    'vod': 'VOD',
    'url': 'URL'
  };
  return types[type] || type;
};

const transformPanelOffset = (media, idx) => {
  const posFromEnd = media.length - 1 - idx

  // if (posFromEnd === 0) return '-120px' 
  // if (posFromEnd === 1) return '-70px'
  // if (posFromEnd === 2) return '-15px'
  return ''
}

const handleDragStart = (e) => {
  e.dataTransfer.setData('text/plain', e.currentTarget.dataset.index)
}
const handleDragEnter = (e) => {
  e.currentTarget.classList.add('hover')
}
const handleDragLeave = (e) => {
  e.currentTarget.classList.remove('hover')
}
const handleDragOver = (e) => {
  e.preventDefault()
}
const handleDrop = async (e, media, flowId) => {
  e.preventDefault()

  const fromIndex = parseInt(e.dataTransfer.getData('text/plain'))
  const toIndex = parseInt(e.target.closest('div.drag-item').dataset.index)

  Array.from(e.currentTarget.parentNode.children).forEach(tr => {
    tr.classList.remove('hover')
  })

  if (fromIndex === toIndex) { // same pos
    return
  }

  try {
    store.dispatch('Mediaflows/reorderFlowMedia', {
      mediaflowId: mediaflow.value._id,
      flowId: flowId,
      fromIndex,
      toIndex
    })

    await MediaflowService.reorderFlowMedia(
      mediaflow.value._id,
      flowId,
      fromIndex,
      toIndex
    )

    notify({ group: 'success', text: 'Media order updated' })
  } catch (err) {
    console.error(err)
    notify({ group: 'error', text: 'Failed to update media order' })
  }
}

const debouncedVolumeUpdate = _.debounce(async (mediaflowId, flowId, mediaId, volume) => {
  try {
    await MediaflowService.updateFlowMedia(
      mediaflowId,
      flowId,
      mediaId,
      { volume }
    );
    notify({ group: 'success', text: 'Volume updated' });
  } catch (err) {
    console.error(err);
    notify({ group: 'error', text: 'Failed to update volume' });
  }
}, 500); // 500ms delay

const debouncedAudioSyncUpdate = _.debounce(async (mediaflowId, flowId, mediaId, offset) => {
  try {
    await MediaflowService.setAudioSync(
      mediaflowId,
      flowId,
      mediaId,
      offset
    );

    // Update audioOffset in store
    store.dispatch('Mediaflows/updateFlowMediaAudioOffset', {
      mediaflowId,
      flowId,
      mediaId,
      audioOffset: offset
    });

    notify({ group: 'success', text: 'Audio sync updated' });
  } catch (err) {
    console.error(err);
    notify({ group: 'error', text: 'Failed to update audio sync' });
  }
}, 200);

const onVolumeChange = async (event, flowId, mediaId) => {
  let volume = parseInt(event.target.value);

  // Clamp volume between 0 and 100
  if (isNaN(volume)) volume = 50;
  volume = Math.max(0, Math.min(100, volume));

  // Update the volume in Vuex store immediately for responsive UI
  store.dispatch('Mediaflows/updateFlowMediaVolume', {
    mediaflowId: mediaflow.value._id,
    flowId,
    mediaId,
    volume
  });

  // Debounced update to backend
  debouncedVolumeUpdate(
    mediaflow.value._id,
    flowId,
    mediaId,
    volume
  );
}

const ensureVolumeRange = (event, flowId, mediaId) => {
  let volume = parseInt(event.target.value);

  // Ensure volume is within range
  if (isNaN(volume) || volume < 0) {
    volume = 0;
  } else if (volume > 100) {
    volume = 100;
  }

  // Update the input value to the clamped value
  event.target.value = volume;

  // Trigger volume change with corrected value
  onVolumeChange(event, flowId, mediaId);
}

const mediaPreviewAvailable = (media) => {
  if (media.type === 'live') {
    return true
  }

  if (media.type === 'vod' && (media?.file ?? '').includes('mp4')) {
    return true
  }

  if (media.embedUrl) {
    return true
  }

  return false
}

const toggleMuteVolume = async (flowId, mediaId) => {
  // Get current muted state from the store
  const flow = mediaflow.value.flows.find(f => f._id === flowId);
  const media = flow?.media?.find(m => m.id === mediaId);
  const currentMuted = media?.muted ?? false;

  const newMutedState = !currentMuted;

  try {
    // Call the mute API
    await MediaflowService.muteFlowMedia(
      mediaflow.value._id,
      flowId,
      mediaId,
      newMutedState
    );

    // Update muted state in store
    store.dispatch('Mediaflows/updateFlowMediaMuted', {
      mediaflowId: mediaflow.value._id,
      flowId,
      mediaId,
      muted: newMutedState
    });

    notify({
      group: 'success',
      text: newMutedState ? 'Muted' : 'Unmuted'
    });
  } catch (err) {
    console.error(err);
    notify({
      group: 'error',
      text: 'Failed to toggle mute'
    });
  }
}

const adjustVolume = (flowId, mediaId, direction) => {
  // Get current volume from the store
  const flow = mediaflow.value.flows.find(f => f._id === flowId);
  const media = flow?.media?.find(m => m.id === mediaId);
  const currentVolume = media?.volume ?? 50;

  // Increment or decrement by 5
  let newVolume = direction === 'up' ? currentVolume + 5 : currentVolume - 5;

  // Clamp between 0 and 100
  newVolume = Math.max(0, Math.min(100, newVolume));

  // Update store and backend
  store.dispatch('Mediaflows/updateFlowMediaVolume', {
    mediaflowId: mediaflow.value._id,
    flowId,
    mediaId,
    volume: newVolume
  });

  debouncedVolumeUpdate(
    mediaflow.value._id,
    flowId,
    mediaId,
    newVolume
  );
}

const onAudioSyncChange = async (event, flowId, mediaId) => {
  let offset = parseInt(event.target.value);

  if (isNaN(offset)) offset = 0;

  offset = Math.max(0, offset);
  offset = Math.min(1000, offset);

  event.target.value = offset;

  // Debounced update to backend
  debouncedAudioSyncUpdate(
    mediaflow.value._id,
    flowId,
    mediaId,
    offset
  );
}

const initializeDetailState = () => {
  destination.value = mediaflow.value?.destination ?? null;
  selectedResolution.value = mediaflow.value?.resolutionPreset ?? '720p';
  selectedResolutionAlias.value = getResolvedScreenResolution(
    selectedResolution.value,
    mediaflow.value?.screenResolution
  );
  const flows = mediaflow.value?.flows || [];
  selectedFlowForSources.value = flows.find(flow => flow.active === true) || flows[0] || null;
};

const loadMediaflow = async (id) => {
  const token = ++detailRequestToken.value;
  const isCurrentRequest = () => (
    scopeAlive.value
    && token === detailRequestToken.value
    && String(route.params.id) === String(id)
  );
  let seedSnapshot = null;

  detailResolving.value = true;
  detailSyncing.value = true;
  adSchedules.value = [];
  store.dispatch('Mediaflows/setTeamPermissionError', { error: false });

  const seedFromList = async () => {
    if (!isCurrentRequest() || mediaflowReady.value) return false;
    const seeded = await store.dispatch('Mediaflows/seedMediaflow', id);
    if (!seeded || !isCurrentRequest()) return false;
    initializeDetailState();
    seedSnapshot = JSON.stringify(mediaflow.value);
    detailResolving.value = false;
    void fetchAdSchedules(id);
    return true;
  };

  await seedFromList();

  void store.dispatch('Streams/getStreams').catch((err) => {
    if (isCurrentRequest()) console.error('Failed to load streams', err);
  });
  void store.dispatch('Mediaflows/getMediaflows').then(() => seedFromList()).catch((err) => {
    if (isCurrentRequest()) console.error('Failed to synchronize Cloud Productions', err);
  });

  try {
    const freshMediaflow = await store.dispatch('Mediaflows/fetchMediaflow', id);
    if (!isCurrentRequest()) return;

    const seededStateUnchanged = seedSnapshot !== null
      && JSON.stringify(mediaflow.value) === seedSnapshot;
    if (seedSnapshot === null || seededStateUnchanged) {
      await store.dispatch('Mediaflows/applyMediaflow', freshMediaflow);
      if (!isCurrentRequest()) return;
      initializeDetailState();
      void fetchAdSchedules(id);
    }
    detailResolving.value = false;
  } catch (err) {
    if (!isCurrentRequest()) return;
    console.error('Failed to load mediaflow data', err);
    if (err && err.toString().includes('not have access to')) {
      store.dispatch('Mediaflows/setTeamPermissionError', { error: true });
    }
    notify({ group: "error", text: "Cloud Production load failed" });
    await router.push({ name: "MediaflowDashboard" });
  } finally {
    if (isCurrentRequest()) detailSyncing.value = false;
  }
};

onMounted(() => {
  void loadMediaflow(route.params.id);
});

watch(
  () => route.params.id,
  (id, previousId) => {
    if (previousId !== undefined && String(id) !== String(previousId)) {
      void loadMediaflow(id);
    }
  }
);

</script>

<template>
  <div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start"
    :data-detail-syncing="detailSyncing || undefined">
    <Spinner v-if="detailResolving || !mediaflowReady" text="Retrieving data..." classes="text-surface-8 mt-96"
      spinner-color="var(--c-dark-8)" spinner-size="15px" />
    <main v-else-if="(!hasLiveSubscription || isLiveSubscriptionExpired || maxMediaflows === 0)">
      <div class="text-center mt-96">
        <div class="max-w-md mx-auto">
          <IconMediaflowLockedPlaceholderImage class="mx-auto mb-4" />
          <h2 class="text-xl font-medium mb-2">Cloud Production is not available</h2>
          <p class="text-dark-6 mb-4">Upgrade your plan to access Cloud Production features</p>
          <FormButton type="main" to="/subscribe">Upgrade Plan</FormButton>
        </div>
      </div>
    </main>
    <main v-else-if="!mediaflowTrial">
      <div class="text-center mt-96">
        <div class="max-w-md mx-auto">
          <h2 class="text-xl font-medium mb-2">Access restricted </h2>
          <p class="text-dark-6 mb-4">Access to Cloud Production restricted</p>
        </div>
      </div>
    </main>
    <main v-else>
      <header class="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div class="flex min-w-0 items-center justify-between">
          <div class="flex mb-4 mt-6 md:mt-12 items-center">
            <BackButton to="/cloud-production" class="!hidden md:!inline-flex mr-2" />

            <Title replaceInnerText :title="mediaflow.name" :editable="true" @changed="onNameChange" />
          </div>
        </div>
        <div class="flex w-full sm:w-auto items-center justify-end gap-2 sm:gap-3">
          <HeaderHelperLinks
            howItWorksLink="https://docs.castr.com/en/articles/12310739-cloud-production-live-switching-made-simple"
            howItWorksText="How Cloud Production works" tutorialLink="cmfns2a1v0wt71d3n7eh7i6bt" />

          <FormButton @click="toggleMediaflow" :disabled="mediaPulsePreview?.alive" type="secondary" size="md"
            class="ml-auto sm:ml-2">
            <span v-if="mediaflow.enabled">Disable</span>
            <span v-else>Enable</span>
          </FormButton>
          <FormButton @click="onDeleteMediaflowRequest" :disabled="mediaPulsePreview?.alive" isIcon type="danger"
            size="md">
            <icon-base class="mx-auto">
              <IconBin />
            </icon-base>
          </FormButton>
        </div>
      </header>

      <div>
        <div class="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-4 xl:gap-1">
          <div class="w-full xl:flex-1 xl:basis-[40%] bg-red-200 rounded-lg p-0">
            <label class="text-s-l text-light-2 text-500">Preview</label>
            <div class="bg-surface-1 rounded-lg p-0">
              <div class="aspect-video bg-surface-2 rounded flex items-center justify-center overflow-hidden"
                style="position: relative;">
                <span v-if="!mediaflow.enabled" class="text-dark-6">Cloud Production disabled</span>
                <span v-else-if="!destinationStream" class="text-dark-6">No destination set</span>
                <span
                  v-else-if="destinationStream && !mediaPulsePreview || destinationStream && !mediaPulsePreview.alive"
                  class="text-dark-6">No preview available</span>
                <StreamPlayer v-else-if="destinationStream" :obs="true" :mediaPulse="mediaPulsePreview"
                  :stream="{ ...mediaflow, key: mediaflow.key + '_preview' }" :streamUrl="''" :multiview="false"
                  :refLink="0" :isPreview="false" :portrait="isPortraitResolution" />
              </div>
            </div>
          </div>
          <div class="w-full xl:basis-[10%] bg-green-200 text-center rounded-lg p-2">
            <div class="mb-0">
              <label class="block text-left text-md font-medium mb-1 text-dark-8">Transition</label>

              <drop-down-menu ref="transitionDropdown" type="flat" size="md" fullWidth>
                <template #toggle-button>
                    <span class="">{{ sceneTransition }}</span>
                    <icon-base class="ml-auto">
                      <icon-chevron-down />
                    </icon-base>
                </template>

                <template #menu-items>
                  <drop-down-menu-item
                    v-for="option in [{ label: 'Fade', value: 'Fade' }, { label: 'Cut', value: 'Cut' }]"
                    :key="option.value" @click="onTransitionTypeSelect(option)">
                    <span class="ml-2">{{ option.label }}</span>
                  </drop-down-menu-item>
                </template>
              </drop-down-menu>
            </div>
            <div class="mt-2 transition-duration-input-wrapper">
              <input type="number" class="transition-duration-input"
                :value="parseInt(sceneTransitionDuration.toString().replace(' ms', '').trim()) || 300" min="50"
                max="10000" step="50" @input="onTransitionDurationChange" @blur="ensureTransitionDurationRange"
                placeholder="300" title="Transition duration in milliseconds (50-10000ms range)" />
              <span class="transition-duration-unit">ms</span>
              <div class="transition-duration-arrows">
                <FormButton custom-classes="transition-duration-arrow transition-duration-arrow-up"
                  @click="adjustTransitionDuration('up')" title="Increase duration (+50ms)">
                  <icon-base :width="8" :height="5" :width-view-box="8" :height-view-box="5">
                    <icon-triangle-up />
                  </icon-base>
                </FormButton>
                <FormButton custom-classes="transition-duration-arrow transition-duration-arrow-down"
                  @click="adjustTransitionDuration('down')" title="Decrease duration (-50ms)">
                  <icon-base :width="8" :height="5" :width-view-box="8" :height-view-box="5">
                    <icon-triangle-down />
                  </icon-base>
                </FormButton>
              </div>
            </div>

            <FormButton type="main" :fullWidth="true" class="justify-center mt-2 transition-btn" size="md"
              @click="onTransition">
              {{ sceneTransitionProcessing ? 'Transitioning...' : 'Transition' }}
            </FormButton>
          </div>

          <div class="w-full xl:flex-1 xl:basis-[40%] bg-blue-200 rounded-lg p-0">
            <label class="text-s-l text-light-2 text-500">Production</label>
            <div class="bg-surface-1 rounded-lg p-0">
              <div class="aspect-video bg-surface-2 rounded flex items-center justify-center overflow-hidden"
                style="position: relative;">
                <span v-if="!mediaflow.enabled" class="text-dark-6">Cloud Production disabled</span>
                <span v-else-if="!destinationStream" class="text-dark-6">No destination set</span>
                <span v-else-if="destinationStream && !mediaPulse || destinationStream && !mediaPulse.alive"
                  class="text-dark-6">No preview available</span>
                <StreamPlayer v-else-if="destinationStream" :obs="true" :mediaPulse="mediaPulse" :stream="mediaflow"
                  :streamUrl="''" :multiview="false" :refLink="1" :portrait="isPortraitResolution" />
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs Navigation -->
        <div class="mt-4 mb-4">
          <TabGroup class="w-full" type="lines">
            <TabItem type="line" @click="setTab('flow')" label="Flow" :isActive="isTabActive('flow')" />
            <TabItem type="line" @click="setTab('ad-schedules')" label="Ad schedules"
              :isActive="isTabActive('ad-schedules')">
              <Badge type="main" class="badge_beta ml-2">Beta</Badge>
            </TabItem>
          </TabGroup>
        </div>

        <div v-if="isTabActive('flow')" class="grid grid-cols-1 xl:grid-cols-12 gap-4 mt-3">
          <!-- Flows Region -->
          <div class="xl:col-span-3 bg-surface-2 rounded-lg p-3">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-medium m-0">Flows</h3>
              <Tooltip v-if="mediaflow.flows && mediaflow.flows.length >= maxMediaflowFlows" class="email-tooltip"
                :content="'Please upgrade your subscription to add more flows'">
                <FormButton :disabled="true" type="secondary" size="sm" @click="openAddFlowModal">
                  <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="text-dark-9">
                    <icon-plus-small />
                  </icon-base>

                  <span class="ml-2 text-sm">New flow</span>
                </FormButton>
              </Tooltip>
              <FormButton v-else :disabled="!mediaflow.enabled" type="secondary" size="md" @click="openAddFlowModal">
                <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="text-dark-9">
                  <icon-plus-small />
                </icon-base>

                <span class="ml-2 text-sm">New flow</span>
              </FormButton>
            </div>

            <draggable v-model="mediaflow.flows" class="space-y-1" :animation="200" handle=".flow-drag-handle"
              @end="onFlowsReorder">
              <template #item="{ element: flow }">
                <div class="bg-surface-2 rounded p-2 cursor-pointer transition-all hover:bg-surface-3 text-sm"
                  :class="{ 'bg-surface-3': flow.active }" @click="onFlowClick(flow)">
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-2 flex-1 min-w-0">
                      <icon-base class="mr-3 text-white" :width="20" :height="14" :width-view-box="20"
                        :height-view-box="14">
                        <icon-mediaflow-flow-lines />
                      </icon-base>
                      <span class="font-medium truncate">{{ flow.name }}</span>
                    </div>
                    <FormButton isIcon type="danger" size="md" @click.stop="onFlowEdit(flow)"
                      class="flex-shrink-0 ml-1">
                      <icon-base class="mx-auto">
                        <IconEdit />
                      </icon-base>
                    </FormButton>
                    <FormButton isIcon type="danger" size="md" @click.stop="onFlowDelete(flow)"
                      class="flex-shrink-0 ml-2">
                      <icon-base class="mx-auto">
                        <IconBin />
                      </icon-base>
                    </FormButton>
                  </div>
                </div>
              </template>
            </draggable>

            <div v-if="!mediaflow.flows || mediaflow.flows.length === 0"
              class="flex flex-col items-center justify-center py-12" style="height: 80%;">
              <div class="w-10 h-10 border-[1px] border-step-color rounded-full flex items-center justify-center mb-2">
                <span class="text-lg font-medium text-white">1</span>
              </div>
              <p class="text-dark-8 text-sm font-medium">Create a Flow</p>
            </div>
          </div>

          <!-- Sources Region -->
          <div class="xl:col-span-6 bg-surface-2 rounded-lg p-3">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-base font-medium m-0">{{ selectedFlowForSources?.name || 'Flow' }}: Sources</h3>
              <Tooltip
                v-if="selectedFlowForSources && selectedFlowForSources.media && selectedFlowForSources.media.length >= maxMediaflowMedia"
                class="email-tooltip" :content="'Please upgrade your subscription to add more media'">
                <FormButton :disabled="true" type="secondary" size="md">
                  <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="text-dark-9">
                    <icon-plus-small />
                  </icon-base>

                  <span class="ml-2 text-sm">Add sources</span>
                </FormButton>
              </Tooltip>
              <FormButton v-else :disabled="!selectedFlowForSources || !mediaflow.enabled" type="secondary" size="md"
                @click="openAddSourceModal">
                <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="text-dark-9">
                  <icon-plus-small />
                </icon-base>

                <span class="ml-2 text-sm">Add sources</span>
              </FormButton>
            </div>

            <div
              v-if="!selectedFlowForSources || !selectedFlowForSources.media || (selectedFlowForSources.media && selectedFlowForSources.media.length === 0)"
              class="flex flex-col items-center justify-center py-2 space-y-6">
              <div class="flex flex-col items-center space-y-0">
                <div class="flex flex-col items-center">
                  <div
                    class="w-10 h-10 border-[1px] border-step-color rounded-full flex items-center justify-center mb-2">
                    <span class="text-lg font-medium text-white">2</span>
                  </div>
                  <p class="text-dark-8 text-sm font-medium">Add a source</p>
                  <div class="flex flex-col space-y-1 py-2">
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                  </div>
                </div>

                <div class="flex flex-col items-center">
                  <div
                    class="w-10 h-10 border-[1px] border-step-color rounded-full flex items-center justify-center mb-2">
                    <span class="text-lg font-medium text-white">3</span>
                  </div>
                  <p class="text-dark-8 text-sm font-medium">Select a destination</p>
                  <div class="flex flex-col space-y-1 py-2">
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                  </div>
                </div>

                <div class="flex flex-col items-center">
                  <div
                    class="w-10 h-10 border-[1px] border-step-color rounded-full flex items-center justify-center mb-2">
                    <span class="text-lg font-medium text-white">4</span>
                  </div>
                  <p class="text-dark-8 text-sm font-medium">Ready to start stream</p>
                  <div class="flex flex-col space-y-1 py-2">
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                    <div class="w-1 h-1 bg-[#404B72] rounded-full"></div>
                  </div>
                </div>

                <div class="flex flex-col items-center">
                  <div
                    class="w-10 h-10 border-[1px] border-step-color rounded-full flex items-center justify-center mb-2">
                    <span class="text-lg font-medium text-white">5</span>
                  </div>
                  <p class="text-dark-8 text-sm font-medium">Transition to Production</p>
                </div>
              </div>
            </div>

            <div v-for="(media, idx) in selectedFlowForSources?.media || []" :key="media.id">
              <div class="bg-surface-2 rounded py-2 text-sm relative order-item" :style="{
                borderBottom: idx === (selectedFlowForSources?.media?.length || 0) - 1 ? 'none' : '1px solid var(--c-dark-3)',
              }">
                <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                  <div :key="idx" :data-index="idx" draggable="true"
                    @dragstart="(event) => handleDragStart(event, media)"
                    @dragenter="(event) => handleDragEnter(event, media)"
                    @dragleave="(event) => handleDragLeave(event, media)"
                    @dragover="(event) => handleDragOver(event, media)"
                    @drop="(event) => handleDrop(event, media, selectedFlowForSources._id)"
                    class="flex items-center gap-1 flex-1 min-w-0 drag-item">
                    <icon-base class="mr-2 text-white" :width="20" :height="14" :width-view-box="20"
                      :height-view-box="14">
                      <icon-mediaflow-flow-lines />
                    </icon-base>

                    <div class="flex flex-col min-w-0 flex-1">
                      <span class="font-medium text-md truncate">{{ media.name }}</span>
                      <span class="text-xs text-surface-6">{{ getMediaTypeName(media.type) }}</span>
                    </div>
                  </div>

                  <div v-if="media.type === 'live' || media.type === 'vod'" class="flex flex-wrap items-center gap-2 w-full lg:w-auto lg:max-w-[40%]">
                    <div class="flex items-center">
                      <div class="flex-shrink-0 cursor-pointer volume-icon-container"
                        @click="toggleMuteVolume(selectedFlowForSources._id, media.id)"
                        :title="media.muted ? 'Unmute' : 'Mute'">
                        <icon-base :width="27" :height="27" :width-view-box="32" :height-view-box="32" fill="none"
                          class="volume-icon" :class="{ 'volume-muted': media.muted }">
                          <icon-mediaflow-volume-indicator />
                          <!-- Mute slash indicator -->
                          <line v-if="media.muted" x1="8" y1="8" x2="24" y2="24" stroke="#f04747" stroke-width="2.5"
                            stroke-linecap="round" />
                        </icon-base>
                      </div>
                      <div class="ml-1 flex-1 volume-input-wrapper">
                        <input type="number" class="volume-input" :value="media.volume ?? 100" min="0" max="100"
                          @input="onVolumeChange($event, selectedFlowForSources._id, media.id)"
                          @blur="ensureVolumeRange($event, selectedFlowForSources._id, media.id)" />
                        <span class="volume-percent">%</span>
                        <div class="volume-arrows">
                          <FormButton custom-classes="volume-arrow volume-arrow-up"
                            @click="adjustVolume(selectedFlowForSources._id, media.id, 'up')"
                            title="Increase volume (+5)">
                            <icon-base :width="8" :height="5" :width-view-box="8" :height-view-box="5">
                              <icon-triangle-up />
                            </icon-base>
                          </FormButton>
                          <FormButton custom-classes="volume-arrow volume-arrow-down"
                            @click="adjustVolume(selectedFlowForSources._id, media.id, 'down')"
                            title="Decrease volume (-5)">
                            <icon-base :width="8" :height="5" :width-view-box="8" :height-view-box="5">
                              <icon-triangle-down />
                            </icon-base>
                          </FormButton>
                        </div>
                      </div>
                    </div>

                    <!-- Audio Sync Offset Control -->
                    <div class="flex items-center">
                      <span class="text-xs text-surface-6 mr-1 flex-shrink-0" title="Audio Sync Offset">Sync</span>
                      <div class="audio-sync-input-wrapper">
                        <input type="number" class="audio-sync-input" :value="media.audioOffset ?? 0" min="0" max="1000"
                          step="10" @input="onAudioSyncChange($event, selectedFlowForSources._id, media.id)"
                          placeholder="0" title="Audio sync offset in milliseconds (0-1000ms range)" />
                        <span class="audio-sync-unit">ms</span>
                      </div>
                    </div>
                  </div>

                  <div class="flex w-full lg:w-auto items-center justify-end gap-1 flex-shrink-0">
                    <FormSwitch class="" size="md" v-model="media.show"
                      @change="onMediaToggle(selectedFlowForSources?._id, media.id)" />

                    <FormButton v-if="mediaPreviewAvailable(media)" class="mr-1" isIcon size="md" type="danger"
                      @click="openMediaPlayerModal(media)">
                      <icon-base :width="18" :height="18" :width-view-box="16" :height-view-box="16" fill="none"
                        class="text-dark-7">
                        <icon-corner-frame />
                      </icon-base>
                    </FormButton>
                    <div v-else style="width: 36px; height: 18px;">
                    </div>

                    <Tooltip class="text-underline text-dark-9 text-500 text-md" :content="'Position & resize'">
                      <MediaTransformDropdownPanel :disabled="!streamAlive" class="mr-1"
                        :topOffset="transformPanelOffset(selectedFlowForSources.media, idx)"
                        :mediaflowId="mediaflow._id" :flowId="selectedFlowForSources._id" :media="media"
                        @media-updated="onMediaUpdated" />
                    </Tooltip>

                    <FormButton class="mr-1" isIcon type="danger" size="md"
                      @click="onMediaEdit(selectedFlowForSources?._id, media)">
                      <icon-base class="mx-auto">
                        <IconEdit />
                      </icon-base>
                    </FormButton>

                    <FormButton class="" isIcon type="danger" size="md"
                      @click="onMediaDelete(selectedFlowForSources?._id, media.id)">
                      <icon-base class="mx-auto">
                        <IconBin />
                      </icon-base>
                    </FormButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Control Panel Column -->
          <div class="xl:col-span-3 bg-surface-2 rounded-lg p-2">
            <div class="rounded-lg p-2">
              <div class="space-y-3">
                <FormButton v-if="!mediaflow.obsInstance"
                  :disabled="startStreamProcessing || !mediaflow.enabled || mediaflow.flows.length === 0 || !destination"
                  type="main" :fullWidth="true" class="justify-center production-control-btn" size="lg"
                  @click="onStreamStart">
                  {{ startStreamProcessing ? 'Starting Production...' : 'Start Production' }}
                </FormButton>
                <FormButton v-else :disabled="startStreamProcessing || !mediaflow.enabled" type="danger"
                  :fullWidth="true" class="justify-center production-control-btn" size="lg"
                  @click="showStopStreamModal">
                  {{ startStreamProcessing ? 'Stopping Production...' : 'Stop Production' }}
                  <span v-if="mediaflow.flowInstanceReservedAt && mediaflow.flowInstanceReserveTimeSec"
                    class="ml-2 text-xs text-dark-6">
                    ({{ formattedObsCountdown }})
                  </span>
                </FormButton>

                <div class="p-1">
                  <div class="flex justify-between items-center" style="">
                    <label class="text-s-l text-light-2 text-500">Destination</label>
                  </div>
                  <div class="mt-2 relative">
                    <div class="flex justify-between items-center text-dark-6 mb-2 mt-4 md:mt-0">
                      <h6 class="text-dark-8 text-s-s text-500 m-0">Link to</h6>
                      <FormButton v-if="destination" type="light" size="xs" class="p-0 text-s-s text-reset border-0"
                        @click="setDestination(null)">Remove
                      </FormButton>
                    </div>

                    <stream-source-dropdown :disabled="hasLiveSubscription && !hasLiveSubscription.enabled"
                      @on-show="onSourceDropdownShow" @on-hide="onSourceDropdownHide"
                      @on-source-selected="setDestination" :dropRight="false" :selectedStream="destinationStream"
                      :showPlaylist="false" source="dynamic" :refLink="0" :onlyLiveSelector="true"
                      :disabledStreams="disabledStreamIds" />
                  </div>

                </div>

                <div class="actions-sect mt-2">
                  <h6 class="mb-2 text-dark-8 text-sm text-500 m-0">Quick Actions</h6>
                  <div class="flex gap-2">
                    <div
                      class="flex-1 border rounded-xl px-4 py-3 flex flex-col items-center justify-center cursor-pointer hover:bg-surface-3 transition-colors"
                      style="border-color: #404B72 !important;" @click="onStreamPage">
                      <icon-base :width="17" :height="17" :width-view-box="17" :height-view-box="17"
                        class="text-[#0062FF]">
                        <icon-open-external />
                      </icon-base>
                      <p class="text-s-s text-dark-9 mt-2 mb-0 text-center">Stream</p>
                    </div>

                    <div
                      class="flex-1 border rounded-xl px-4 py-3 flex flex-col items-center justify-center cursor-pointer hover:bg-surface-3 transition-colors"
                      style="border-color: #404B72 !important;" @click="openStreamConfigModal">
                      <icon-base :width="20" :height="20" fill="none" class="text-dark-8">
                        <icon-cog />
                      </icon-base>

                      <p class="text-s-s text-dark-9 mt-2 mb-0 text-center">Configure</p>
                    </div>

                  </div>

                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Ad Schedules Tab Content -->
      <div v-if="isTabActive('ad-schedules')" class="mt-4">
        <div v-if="!mediaflowAdSchedule">
          <div class="text-center mt-4">
            <div class="max-w-md mx-auto">
              <p class="text-dark-6 mb-4">This feature is not included in your current plan</p>
            </div>
          </div>
        </div>
        <div v-else class="">
          <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <p class="text-surface-8 text-sm mb-0">
              Create and manage automated ad schedules with different trigger types and timing options.
            </p>
            <Tooltip v-if="adSchedules && adSchedules.length >= maxMediaflowAdSchedule" class="email-tooltip"
              :content="'Please upgrade your subscription to add more Ad Schedules'">
              <FormButton :disabled="true" type="secondary" size="md">
                <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="mr-2">
                  <icon-plus-small />
                </icon-base>

                <span>Add New Schedule</span>
              </FormButton>
            </Tooltip>
            <FormButton v-else type="primary" size="md" @click="openAddAdScheduleModal">
              <icon-base :width="12" :height="12" :width-view-box="14" :height-view-box="14" class="mr-2">
                <icon-plus-small />
              </icon-base>
              <span>Add New Schedule</span>
            </FormButton>
          </div>

          <div v-if="adSchedules.length === 0" class="text-center py-12">
            <h3 class="text-lg font-medium text-white mb-2">Ad Schedules</h3>
            <p class="text-dark-6 text-sm">No ad schedules created yet. Create your first schedule to get started.</p>
          </div>

          <div v-else class="space-y-3">
            <div v-for="schedule in adSchedules" :key="schedule._id"
              class="bg-dark-2 rounded-lg px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-dark-3 flex items-center justify-center">
                  <component :is="getScheduleIcon(schedule.type)" />
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="text-base font-medium text-white truncate flex items-center">{{ schedule.name }}
                    <Badge v-if="schedule.enabled" type="online" class="badge_active ml-2">Active</Badge>
                  </h4>
                  <p class="text-xs text-dark-7 mb-1">{{ getScheduleTypeLabel(schedule.type) }}</p>
                </div>
              </div>
              <div class="flex w-full sm:w-auto items-center justify-end gap-2 flex-shrink-0 sm:ml-4">
                <FormButton :type="'secondary'" size="md" :disabled="togglingScheduleId === schedule._id"
                  @click="toggleSchedule(schedule)" class="">

                  <icon-base v-if="!schedule.enabled" class="mr-2 text-dark-9" :width="16" :height="16">
                    <icon-play />
                  </icon-base>

                  {{ schedule.enabled ? 'Deactivate' : 'Activate' }}
                </FormButton>
                <FormButton isIcon type="danger" size="md" @click="onScheduleEdit(schedule)">
                  <IconBase class="mx-auto">
                    <IconEdit />
                  </IconBase>
                </FormButton>
                <FormButton isIcon type="danger" size="md" :disabled="streamAlive || streamAlivePreview"
                  @click="onScheduleDelete(schedule)">
                  <IconBase class="mx-auto">
                    <IconBin />
                  </IconBase>
                </FormButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Stream Configuration Modal -->
    <ModalBox v-model="showStreamConfigModal" title="Stream Configuration" size="sm" placement="center"
      @close="closeStreamConfigModal">
      <!-- Form -->
      <div class="px-6 space-y-4">
        <!-- Resolution Dropdown -->
        <div>
          <label class="block text-sm text-surface-6 mb-2">Resolution</label>
          <drop-down-menu ref="resolutionDropdown" v-if="mediaflowResolution === 1080" type="flat" fullWidth>
            <template #toggle-button>
              {{ selectedResolutionAlias }}
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item v-for="option in resolutionTypes" :key="option.label"
                @click="onResolutionSelect(option)">
                <span class="ml-2">{{ option.label }}</span>
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
          <drop-down-menu ref="resolutionDropdown" v-if="mediaflowResolution === 720" type="flat" fullWidth>
            <template #toggle-button>
              {{ selectedResolutionAlias }}
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item v-for="option in resolutionTypes720Allowed" :key="option.label"
                @click="onResolutionSelect(option)">
                <span class="ml-2">{{ option.label }}</span>
              </drop-down-menu-item>

              <Tooltip v-for="option in resolutionTypes720Locked" :key="option.label"
                class="email-tooltip text-underline text-dark-9 text-500 text-md"
                :content="'Please upgrade your subscription to use 1080p preset'">
                <drop-down-menu-item disabled="true" @click="onResolutionSelect(option)">
                  <span class="ml-2 text-sm font-medium">{{ option.label }}</span>
                </drop-down-menu-item>
              </Tooltip>
            </template>
          </drop-down-menu>
        </div>

        <div>
          <label class="block text-sm text-surface-6 mb-2">Bitrate</label>
          <drop-down-menu ref="bitrateDropdown" type="flat" fullWidth>
            <template #toggle-button>
              {{ streamConfigBitrate }} kbps
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item @click="streamConfigBitrate = '1000', bitrateDropdown.hide()">
                <span class="ml-2">1000 kbps</span>
              </drop-down-menu-item>
              <drop-down-menu-item @click="streamConfigBitrate = '2500', bitrateDropdown.hide()">
                <span class="ml-2">2500 kbps</span>
              </drop-down-menu-item>
              <drop-down-menu-item @click="streamConfigBitrate = '5000', bitrateDropdown.hide()">
                <span class="ml-2">5000 kbps</span>
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
        </div>

        <!-- FPS Dropdown -->
        <div>
          <label class="block text-sm text-surface-6 mb-2">FPS</label>
          <drop-down-menu ref="fpsDropdown" type="flat" fullWidth>
            <template #toggle-button>
              {{ streamConfigFps }} FPS
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item v-for="fps in fpsOptions" :key="fps" @click="streamConfigFps = fps, fpsDropdown.hide()">
                <span class="ml-2">{{ fps }} FPS</span>
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
        </div>
      </div>

      <!-- Footer with Buttons -->
      <template #footer>
        <div class="flex flex-col-reverse sm:flex-row gap-3 p-4 sm:p-6 sm:justify-end" :style="{ marginTop: fpsDropdownOpen ? '80px' : '0' }">
          <FormButton class="w-full justify-center sm:w-auto" type="secondary" size="md" @click="closeStreamConfigModal" :disabled="streamConfigSaving">
            Cancel
          </FormButton>

          <Tooltip v-if="streamAlive || streamAlivePreview" class="email-tooltip"
            :content="'Editing is disabled while Cloud Production is online.'">
            <FormButton class="w-full justify-center sm:w-auto" type="main" size="md" @click="saveStreamConfig" :disabled="true">
              {{ streamConfigSaving ? 'Saving...' : 'Save' }}
            </FormButton>
          </Tooltip>
          <FormButton v-else class="w-full justify-center sm:w-auto" type="primary" size="md" @click="saveStreamConfig" :disabled="streamConfigSaving">
            {{ streamConfigSaving ? 'Saving...' : 'Save' }}
          </FormButton>

        </div>
      </template>
    </ModalBox>

    <ModalBox v-model="showMediaPlayerModal" :title="'Preview: ' + openMediaPlayerSelected.name" size="sm"
      wrapperClasses="w-full md:w-[580px]" placement="center" @close="closeMediaPlayerModal" :headerClass="'p-3'">

      <div class="w-full" :style="[
        openMediaPlayerSelected.type === 'live' ? { 'min-height': '400px' } : { 'height': 'calc(70vh - 120px)', 'min-height': '400px', 'max-height': '500px' }
      ]">
        <!-- Use StreamPlayer for live media -->
        <div v-if="openMediaPlayerSelected.type === 'live'" class="w-full h-full bg-black rounded">
          <StreamPlayer v-if="mediaPlayerStream" :stream="mediaPlayerStream" :mediaPulse="mediaPlayerMediaPulse"
            :streamUrl="`wss://fake/${mediaPlayerStream.key}/mse_ld?tracks=v1a1`" :multiview="false" :obs="true"
            :refLink="2" :portrait="isPortraitResolution" />
        </div>
        <!-- Use iframe for VOD media -->
        <iframe v-else :src="mediaPlayerUrl" class="w-full h-full" style="border: none;" allowfullscreen></iframe>
      </div>
    </ModalBox>

    <demo-video-modal ref="modalDEMOVideo" videoid="cmfns2a1v0wt71d3n7eh7i6bt" />
    <ConfirmModal message="Are you sure you want to delete this cloud production? This action cannot be undone."
      modal-id="delete-mediaflow-content" modal-type="danger" @modal-confirm="onMediaflowDelete" />
    <ConfirmModal message="Are you sure you want to stop this stream?" modal-id="confirm-stop-stream"
      modal-type="danger" @modal-confirm="onConfirmStopStream" />
    <ConfirmModal :modalId="'confirm-delete-flow'"
      :message="'Are you sure you want to delete \'' + (flowToDelete?.name ?? '') + '\' flow? This action cannot be undone.'"
      okText="Delete" cancelText="Cancel" modalType="danger" @modal-confirm="confirmDeleteFlow" />
    <ConfirmModal :modalId="'modal-delete-media'" :message="'Are you sure you want to delete this media?'"
      okText="Delete" cancelText="Cancel" modalType="danger" @modal-confirm="confirmMediaDelete" />
    <ConfirmModal :modalId="'confirm-delete-schedule'"
      :message="'Are you sure you want to delete this ad schedule? This action cannot be undone.'" okText="Delete"
      cancelText="Cancel" modalType="danger" @modal-confirm="confirmDeleteSchedule" />
    <AddFlowModal :mediaflowId="mediaflow._id" ref="addFlowModalRef" @flow-added="onFlowAdded" />
    <EditFlowModal :mediaflowId="mediaflow._id" :flow="flowToEdit" ref="editFlowModalRef"
      @flow-updated="onFlowUpdated" />
    <AddFlowMediaModal :mediaflowId="mediaflow._id" :flowId="selectedFlowForSources?._id" ref="addFlowMediaModalRef"
      @media-added="onMediaAdded" />
    <EditMediaflowModal :mediaflowId="mediaflow._id" :flowId="selectedFlowForSources?._id"
      :media="slectedFlowMediaSource" ref="editMediaflowModalRef" @media-updated="onMediaUpdated"
      :streamAlive="streamAlive || streamAlivePreview" />
    <AddMediaflowAdScheduleModal :mediaflowId="mediaflow._id" ref="addAdScheduleModalRef"
      @schedule-added="onScheduleAdded" @schedule-updated="onScheduleAdded" />
  </div>
</template>

<style scoped>
.badge_active {
  font-size: 11px;
  font-weight: 500;
  padding: 4px 6px;
}

.phone-number-section {
  border: 1px solid #dee2e6;
  border-color: var(--c-dark-3);
  border-radius: 12px;
}

.order-item {
  border-bottom: 1px solid var(--c-dark-3);
  border-top: 0px;
  border-color: var(--c-dark-3);
  border-left: 0px;
  border-right: 0px;
  vertical-align: middle;
}

.volume-icon-container {
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.volume-icon-container:hover {
  opacity: 0.7;
  transform: scale(1.05);
}

.volume-icon-container:active {
  transform: scale(0.95);
}

.volume-icon {
  transition: all 0.2s ease;
}

.volume-muted {
  color: #f04747;
}

.volume-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 72px;
}

.volume-input {
  width: 100%;
  height: 28px;
  padding: 2px 30px 2px 8px;
  background: var(--c-dark-3);
  border: 1px solid var(--c-dark-4);
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  padding-left: 10px;
  outline: none;
  transition: all 0.2s ease;
}

.volume-input:hover {
  border-color: var(--c-dark-5);
  background: var(--c-dark-4);
}

.volume-input:focus {
  border-color: #5865f2;
  background: var(--c-dark-3);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
}

.volume-input::-webkit-inner-spin-button,
.volume-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.volume-input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.volume-percent {
  position: absolute;
  left: 35px;
  color: var(--c-dark-6);
  font-size: 12px;
  font-weight: 400;
  pointer-events: none;
}

.transition-btn {
  min-width: 130px !important;
}

.production-control-btn {
  min-width: 180px !important;
}

.volume-arrows {
  position: absolute;
  right: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.volume-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 12px;
  background: transparent;
  border: none;
  color: var(--c-dark-6);
  cursor: pointer;
  padding: 0;
  transition: all 0.15s ease;
  outline: none;
}

.volume-arrow:hover {
  color: #fff;
  transform: scale(1.1);
}

.volume-arrow:active {
  transform: scale(0.9);
}

.volume-arrow svg {
  width: 8px;
  height: 5px;
}

.audio-sync-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 75px;
}

.audio-sync-input {
  width: 100%;
  height: 28px;
  padding: 2px 26px 2px 8px;
  background: var(--c-dark-3);
  border: 1px solid var(--c-dark-4);
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  padding-left: 10px;
  outline: none;
  transition: all 0.2s ease;
}

.audio-sync-input:hover {
  border-color: var(--c-dark-5);
  background: var(--c-dark-4);
}

.audio-sync-input:focus {
  border-color: #5865f2;
  background: var(--c-dark-3);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
}

.audio-sync-input::-webkit-inner-spin-button,
.audio-sync-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.audio-sync-input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.audio-sync-unit {
  position: absolute;
  left: 45px;
  color: var(--c-dark-6);
  font-size: 11px;
  font-weight: 400;
  pointer-events: none;
}

.transition-duration-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 130px;
}

.transition-duration-input {
  width: 100%;
  height: 35px;
  padding: 2px 26px 2px 8px;
  background: var(--c-dark-3);
  border: 1px solid var(--c-dark-4);
  border-radius: 5px;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  padding-left: 10px;
  outline: none;
  transition: all 0.2s ease;
}

.transition-duration-input:hover {
  border-color: var(--c-dark-5);
  background: var(--c-dark-4);
}

.transition-duration-input:focus {
  border-color: #5865f2;
  background: var(--c-dark-3);
  box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.1);
}

.transition-duration-input::-webkit-inner-spin-button,
.transition-duration-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.transition-duration-input[type=number] {
  appearance: textfield;
  -moz-appearance: textfield;
}

.transition-duration-unit {
  position: absolute;
  left: 90px;
  color: var(--c-dark-6);
  font-size: 11px;
  font-weight: 400;
  pointer-events: none;
}

.transition-duration-arrows {
  position: absolute;
  right: 2px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.transition-duration-arrow {
  width: 16px;
  height: 12px;
  background: transparent;
  border: none;
  color: var(--c-dark-6);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 2px;
}

.transition-duration-arrow:hover {
  color: #fff;
  transform: scale(1.1);
}

.transition-duration-arrow:active {
  transform: scale(0.9);
}

.transition-duration-arrow svg {
  width: 8px;
  height: 5px;
}

.border-step-color {
  border-color: #404B72;
}
</style>

<style>
.e-sidebar-overlay {
  position: fixed !important;
}
</style>
